import { ID } from 'src/types/common';
import {
  Campus,
  Class,
  Institution,
  Professor,
  Program,
} from 'src/types/institution';

export type CourseShort = {
  courseId: ID;
  name: string;
  // authorName: string;
  image: string;
  progress: number;
  courseMaterials: CourseMaterial[];
  // courseScopes: CourseScope[];
  subject: Subject;
  term: Term;
  lastModuleId: ID;
  professors: Professor[];
  identifier: string;
};

export type Course = CourseShort & {
  password?: string;
  addedDate?: string;
  clientCourseMetadata: JSON[];
  courseMetadata?: JSON[];
  created: string;
  description: string;
  downloadsAllowed: boolean;
  downloadsLimit: number;
  endDate: string;
  expirationDate: string;
  expirationMethod: ExpirationMethod;
  expirationPeriod: string;
  isEnabled: boolean;
  enableAi: boolean;
  summary: Summary;
  semanticSearch: SemanticSearch;
  isCompleted: boolean;
  isbn: string;
  keywords: string[];
  lastChangeDate: string;
  professor: Professor;
  startDate: string;
  updated: string;
};

export type CourseMaterial = {
  courseId?: ID;
  courseMaterialId: ID;
  description?: string;
  language?: string;
  extras?: {
    thumbnail?: string;
    subtitles?: string;
  };
  file?: string;
  fileId?: ID;
  identifier?: string;
  image?: string;
  imageId?: ID;
  materialId?: ID;
  materialMetadata?: JSON[];
  media?: Media;
  name: string;
  position?: ID;
  clientCourseMaterialMetadata: JSON;
  summary?: string;
  shortSummary?: string;
  startDate: string | null;
  clientCourseMaterialFlashcards?: MaterialFlashcards;
  clientCourseMaterialHighlights?: Highlight[];
};

export type CourseScope = {
  campus?: Campus;
  campusId?: ID;
  class?: Class;
  classId?: ID;
  courseId?: ID;
  courseScopeId: ID;
  created?: string;
  institution?: Institution;
  institutionId?: ID;
  isEnabled?: ID;
  program?: Program;
  programId?: ID;
  updated?: string;
};

export type Subject = {
  subjectId: ID;
  name: string;
};

export type Term = {
  name?: string;
  seasonFrom?: Season;
  seasonTo?: Season;
  termId: ID;
  yearFrom?: ID;
  yearTo?: ID;
};

export type CourseMaterialDownload = {
  courseMaterialId: ID;
  countDownload: number;
  mashpath: string;
};

export interface Flashcard {
  flashcardId: string;
  created: string;
  flashcardData: FlashcardData;
}

export interface FlashcardData {
  frontSide: string;
  backSide: string;
  deckId?: ID;
  // We only save deckId on the backend; the `deck` is a helper object used only on the frontend
  // it's derived from the deckId and the list of flashcards returned from the API
  deck?: { id: ID };
  confidence?: Confidence;
  originalReference?: {
    pageNumber: number;
    content: { text?: string | null };
  };
}

export type Confidence = 'low' | 'medium' | 'high';

export interface FlashcardDeck {
  id: ID;
  name: string;
}

export type MaterialFlashcards = [FlashcardDecks, ...Flashcard[]];

/**
 * Deck information is stored as a list in flashcardData in the first flashcard returned from the API.
 * It has the unique flashcardId 'decks'.
 */
export interface FlashcardDecks {
  flashcardId: 'decks';
  flashcardData: FlashcardDeck[];
}

export interface Highlight {
  highlightId: string;
  created: string;
  highlightData: JSON;
}

export enum Media {
  AUDIO = 'AUDIO',
  PDF = 'PDF',
  VIDEO = 'VIDEO',
}

export enum Summary {
  OFF = 'OFF',
  SHORT = 'SHORT',
  LONG = 'LONG',
  BOTH = 'BOTH',
}

export enum SemanticSearch {
  OFF = 'OFF',
  SIMPLE = 'SIMPLE',
  ASSISTED = 'ASSISTED',
}

enum ExpirationMethod {
  ABSOLUTE = 'ABSOLUTE',
  NONE = 'NONE',
  RELATIVE = 'RELATIVE',
}

enum Season {
  FALL = 'FALL',
  OTHER = 'OTHER',
  SPRING = 'SPRING',
  SUMMER = 'SUMMER',
  WINTER = 'WINTER',
}
